import Tab from './Tab/Tab';
import { TabBarProps } from './tab-bar-props.interface';
import { Bell, BookOpen, ClipboardList, Gamepad2, ScrollText, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import Home from 'src/assets/images/icons/default/Home';
import LinkIcon from 'src/assets/images/icons/default/Link';
import MoneyBill from 'src/assets/images/icons/default/MoneyBill';
import User from 'src/assets/images/icons/default/UserWhite';
import { useOptionalNotificationInboxContext } from 'src/contexts/NotificationInboxContext/NotificationInboxContext';
import { useUserAccountDetailsContext } from 'src/contexts/UserAccountDetailsContext/UserAccountDetailsContext';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useInviteMenuNotifications } from 'src/hooks/use-invite-menu-notifications';
import { useTranslationByKey } from 'src/hooks/use-translation-by-key';

const SURVEY_TIPS_URL = 'https://rewardbloom.com/en/survey-tips';

interface NavigationTab {
  icon: React.ReactNode;
  label: string;
  path?: string;
  isExternal?: boolean;
}

/**
 * TabBar component displays a simple navigation bar with 3 icons and borders.
 *
 * @returns {JSX.Element} The TabBar component.
 */
const TabBar: React.FC<TabBarProps> = ({ showMyAccountTabWhileOnboarding }) => {
  const t = useTranslationByKey('COMPONENTS.TAB_BAR');
  const { isMobile } = useBreakpoint();
  const { unreadGameInviteCount, unreadProjectInviteCount } = useInviteMenuNotifications();
  const { accountDetails } = useUserAccountDetailsContext();
  const unreadCount = useOptionalNotificationInboxContext()?.unreadCount ?? 0;

  const getNotificationCount = (path?: string) => {
    if (path === '/games') return unreadGameInviteCount;
    if (path === '/surveys') return unreadProjectInviteCount;
    if (path === '/quests') return accountDetails?.questsNotification ? 1 : 0;
    if (path === '/notifications') return unreadCount;
    return 0;
  };

  const primaryTabs: NavigationTab[] = [
    { icon: <Home className="w-5 h-5" />, label: t('HOME'), path: '/home' },
    { icon: <ScrollText className="w-5 h-5" />, label: t('QUESTS'), path: '/quests' },
    { icon: <Gamepad2 className="w-5 h-5" />, label: t('GAMES'), path: '/games' },
    { icon: <ClipboardList className="w-5 h-5" />, label: t('SURVEYS'), path: '/surveys' },
    { icon: <MoneyBill className="w-5 h-5" />, label: t('REWARDS'), path: '/my-rewards' },
    { icon: <Bell className="w-5 h-5" />, label: t('INBOX'), path: '/notifications' },
    { icon: <LinkIcon className="w-5 h-5" />, label: t('AFFILIATE'), path: '/affiliate' },
  ];

  const tabs: NavigationTab[] = isMobile
    ? [...primaryTabs, { icon: <Settings className="w-5 h-5" />, label: t('MY_ACCOUNT'), path: '/my-account' }]
    : [
        ...primaryTabs,
        { icon: <User className="w-5 h-5" />, label: t('MY_ACCOUNT'), path: '/my-account' },
        { icon: <BookOpen className="w-5 h-5" />, label: t('SURVEY_TIPS'), path: SURVEY_TIPS_URL, isExternal: true },
      ];
  const location = useLocation();
  const selectedTabIndex = tabs.findIndex(
    (tab) => !!tab.path && !tab.isExternal && location.pathname.includes(tab.path)
  );

  const navigate = useNavigate();

  return (
    <div
      className={`
      relative flex flex-1 justify-center items-end
      ${isMobile ? 'pb-0' : 'pb-2'}
    `}
    >
      {/* Mobile-optimized background */}
      {isMobile ? (
        <div className="relative w-full z-[110]">
          {/* Mobile: Clean background */}
          <div className="bg-white/95 backdrop-blur-sm border-t border-gray-100 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
            <div className="px-2 py-2">
              <div className="flex items-center justify-between backdrop-blur-sm rounded-2xl p-1">
                {tabs.map((tab, index) => (
                  <Tab
                    key={index}
                    showWhileOnboarding={tab.path === '/my-account' && showMyAccountTabWhileOnboarding}
                    isSelected={index === selectedTabIndex}
                    icon={tab.icon}
                    ariaLabel={tab.label}
                    label={tab.label}
                    notificationCount={getNotificationCount(tab.path)}
                    onClick={() => {
                      if (tab.isExternal) {
                        const newWindow = window.open(tab.path || '', '_blank', 'noopener,noreferrer');
                        if (newWindow) {
                          newWindow.opener = null;
                        }
                        return;
                      }

                      if (tab.path) {
                        navigate(tab.path);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Desktop: Simplified design */
        <div
          className={`
          relative left-1/2 transform -translate-x-1/2 w-[calc(100%-2rem)] max-w-md z-[110]
        `}
        >
          <div className="relative bg-white/95 backdrop-blur-sm border border-gray-200 rounded-3xl shadow-lg p-2">
            {/* Main tab container */}
            <div className="relative flex items-center justify-between bg-gray-50/90 backdrop-blur-sm rounded-2xl p-1.5">
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  showWhileOnboarding={tab.path === '/my-account' && showMyAccountTabWhileOnboarding}
                  isSelected={index === selectedTabIndex}
                  icon={tab.icon}
                  label={tab.label}
                  notificationCount={getNotificationCount(tab.path)}
                  onClick={() => {
                    if (tab.isExternal) {
                      const newWindow = window.open(tab.path || '', '_blank', 'noopener,noreferrer');
                      if (newWindow) {
                        newWindow.opener = null;
                      }
                      return;
                    }

                    if (tab.path) {
                      navigate(tab.path);
                    }
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TabBar;
